// ProposalList.js
import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  Paper,
  Stack,
  Snackbar,
  Alert,
  useTheme,
  Button,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Visibility,
  DragIndicator,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useAuth } from "../../hooks/useAuth";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";
import {
  PROPOSAL_STATUS,
  PROPOSAL_STATUS_LABELS,
} from "../../utils/models/proposalModel";
import { CONTRACT_STATUS } from "../../utils/models/contractModel";

const STATUS_ORDER = [
  PROPOSAL_STATUS.PENDING,
  PROPOSAL_STATUS.CREATED,
  PROPOSAL_STATUS.SENT,
  PROPOSAL_STATUS.MODIFICATION,
  PROPOSAL_STATUS.ACCEPTED,
];

const ITEMS_PER_PAGE = 3;

const ProposalList = ({ onView, onEdit, filter = {} }) => {
  const theme = useTheme();
  const { data: proposals, loading } = useCollection("proposals");
  const { remove, update: updateDoc } = useAuditFirestore("proposals");
  const { data: clients } = useCollection("clients");
  const { data: contacts } = useCollection("contacts");
  const { data: commercials } = useCollection("commercials");
  const { data: roles } = useCollection("roles");
  const { userProfile } = useAuth();
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const { add: addContract } = useAuditFirestore("contracts");
  const [localProposals, setLocalProposals] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});

  React.useEffect(() => {
    if (proposals) {
      setLocalProposals(proposals);
    }
  }, [proposals]);

  // ============================================
  // DETERMINAR SI EL USUARIO ES COMERCIAL (ROL QUE DEBE VER SOLO SUS PROPUESTAS)
  // ============================================
  const isCommercialRole = useMemo(() => {
    if (!userProfile?.role || !roles) return false;

    // Buscar el rol del usuario en la colección de roles dinámicos
    const currentRole = roles.find(
      r => r.id === userProfile.role || r.name === userProfile.role
    );

    if (!currentRole) return false;

    // Verificar si el nombre del rol contiene "comercial" (case insensitive)
    // O si tiene el módulo "proposals" pero sin viewAll
    const roleName = currentRole.name?.toLowerCase() || '';
    const isCommercial = roleName.includes('comercial') || roleName.includes('commercial');
    
    // También verificamos si el módulo proposals tiene viewAll === false
    const hasLimitedProposals = currentRole.modules?.proposals && 
                               currentRole.modules.proposals.viewAll === false;

    return isCommercial || hasLimitedProposals;
  }, [userProfile, roles]);

  // ============================================
  // ENCONTRAR EL COMERCIAL ASOCIADO AL USUARIO (SOLO SI ES COMERCIAL)
  // ============================================
  const userCommercial = useMemo(() => {
    if (!isCommercialRole || !userProfile) return null;

    // Método 1: Buscar por userId en commercials (más confiable)
    let commercial = commercials?.find(
      c => c.userId === userProfile.uid
    );

    // Método 2: Buscar por email en contacts tipo commercial
    if (!commercial && contacts) {
      const contact = contacts.find(
        c => c.contactType === "commercial" && 
            c.email?.toLowerCase() === userProfile.email?.toLowerCase()
      );
      if (contact) {
        commercial = commercials?.find(c => c.contactId === contact.id);
      }
    }

    // Método 3: Buscar por nombre (último recurso)
    if (!commercial && contacts) {
      const fullName = `${userProfile.displayName || ''}`.toLowerCase();
      const contact = contacts.find(
        c => c.contactType === "commercial" && 
            `${c.firstName} ${c.lastName}`.toLowerCase() === fullName
      );
      if (contact) {
        commercial = commercials?.find(c => c.contactId === contact.id);
      }
    }

    return commercial || null;
  }, [isCommercialRole, userProfile, commercials, contacts]);

  // ============================================
  // FILTRO PRINCIPAL
  // ============================================
  const filtered = useMemo(() => {
    if (!localProposals) return [];

    return localProposals.filter((proposal) => {
      // Filtro de activos
      if (proposal.isActive !== true) return false;

      // SOLO filtrar si el usuario es comercial
      if (isCommercialRole) {
        if (!userCommercial) {
          // Si es comercial pero no se encontró su perfil, no mostrar nada
          return false;
        }

        // Verificar si la propuesta está asociada a este comercial
        const isAssignedToCommercial = 
          proposal.entryChannelContactId === userCommercial.contactId;

        if (!isAssignedToCommercial) {
          return false; // No mostrar propuestas de otros comerciales
        }
      }
      // Si NO es comercial, no se aplica ningún filtro adicional

      // Filtro de búsqueda (aplica para todos)
      if (search) {
        const searchLower = search.toLowerCase();
        return (
          proposal.title?.toLowerCase().includes(searchLower) ||
          proposal.clientName?.toLowerCase().includes(searchLower) ||
          proposal.description?.toLowerCase().includes(searchLower) ||
          proposal.commercialName?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }, [localProposals, isCommercialRole, userCommercial, search]);

  const grouped = useMemo(() => {
    const groups = {};

    STATUS_ORDER.forEach((status) => {
      groups[status] =
        filtered
          ?.filter((p) => p.status === status)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)) || [];
    });

    return groups;
  }, [filtered]);

  const getVisibleItems = (status, items) => {
    const max = visibleCounts[status] || ITEMS_PER_PAGE;
    return items.slice(0, max);
  };

  const hasMore = (status, items) => {
    const max = visibleCounts[status] || ITEMS_PER_PAGE;
    return items.length > max;
  };

  const canShowLess = (status) => {
    return (visibleCounts[status] || ITEMS_PER_PAGE) > ITEMS_PER_PAGE;
  };

  const showMore = (status) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [status]: (prev[status] || ITEMS_PER_PAGE) + ITEMS_PER_PAGE,
    }));
  };

  const showLess = (status) => {
    setVisibleCounts((prev) => ({
      ...prev,
      [status]: Math.max(
        ITEMS_PER_PAGE,
        (prev[status] || ITEMS_PER_PAGE) - ITEMS_PER_PAGE,
      ),
    }));
  };

  const handleDragEnd = useCallback(
    async (result) => {
      const { source, destination, draggableId } = result;

      if (!destination) return;

      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) {
        return;
      }

      const newStatus = destination.droppableId;
      const oldStatus = source.droppableId;

      const proposal = localProposals?.find((p) => p.id === draggableId);
      if (!proposal) return;

      let updatedData = {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      };

      if (
        newStatus === PROPOSAL_STATUS.MODIFICATION &&
        oldStatus !== PROPOSAL_STATUS.MODIFICATION
      ) {
        const currentVersion = parseFloat(proposal.version || "1.0");
        const newVersion = (currentVersion + 0.1).toFixed(1);
        const newVersionEntry = {
          version: newVersion,
          date: new Date().toISOString(),
          previousStatus: oldStatus,
          notes: `Modificación desde estado: ${PROPOSAL_STATUS_LABELS[oldStatus]}`,
        };

        updatedData = {
          ...updatedData,
          version: newVersion,
          versions: [...(proposal.versions || []), newVersionEntry],
        };
      }

      setLocalProposals((prev) =>
        prev.map((p) => (p.id === draggableId ? { ...p, ...updatedData } : p)),
      );

      try {
        await updateDoc(draggableId, updatedData);

        if (
          newStatus === PROPOSAL_STATUS.ACCEPTED &&
          oldStatus !== PROPOSAL_STATUS.ACCEPTED
        ) {
          const client = clients?.find((c) => c.id === proposal.clientId);

          await addContract({
            title: proposal.title || "Contrato sin título",
            proposalId: proposal.id,
            proposalTitle: proposal.title,
            clientId: proposal.clientId,
            clientName: proposal.clientName,
            clientType: client?.type || "individual",
            clientDni: client?.dni || client?.taxId || "",
            clientTaxId: client?.taxId || "",
            clientPhone: client?.phone || "",
            clientEmail: proposal.clientEmail,
            address: proposal.address,
            status: CONTRACT_STATUS.PENDING,
            notes: "Contrato generado automáticamente al aceptar propuesta",
            version: "1.0",
            versions: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            createdBy: "",
            isActive: true,
          });

          console.log("✅ Contrato creado automáticamente al aceptar propuesta");
        }
      } catch (error) {
        console.error("Error updating status:", error);
        setLocalProposals((prev) =>
          prev.map((p) =>
            p.id === draggableId ? { ...p, status: oldStatus } : p,
          ),
        );
        setErrorMessage("Error al actualizar el estado");
      }
    },
    [updateDoc, localProposals, addContract, clients],
  );

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setLocalProposals((prev) => prev.filter((p) => p.id !== deleteTarget.id));
    const deletedProposal = deleteTarget;
    setDeleteTarget(null);

    try {
      await remove(deletedProposal.id);
    } catch (error) {
      console.error("Error deleting:", error);
      setLocalProposals((prev) => [...prev, deletedProposal]);
      setErrorMessage("Error al eliminar la propuesta");
    }
  };

  // ============================================
  // RENDERIZADO
  // ============================================
  if (loading || !localProposals || !roles) return <LoadingSpinner />;

  // Si es comercial y no se encuentra su perfil de comercial vinculado
  if (isCommercialRole && !userCommercial) {
    return (
      <EmptyState
        title="Sin acceso"
        description="No se encontró tu perfil de comercial. Contacta al administrador para vincular tu usuario con un comercial."
      />
    );
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: { xs: 2, sm: 3 },
          borderRadius: { xs: "16px", sm: "20px" },
          border: "1px solid",
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.12)"
              : "#e2e8f0",
          background: theme.palette.background.paper,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            placeholder="Buscar por título o cliente"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: "260px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                background: theme.palette.background.paper,
                height: 44,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: theme.palette.primary.main }}
                  />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    ✖
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{ display: "flex", alignItems: "center", height: 44, gap: 1 }}
          >
            {/* Mostrar info del comercial si aplica */}
            {isCommercialRole && userCommercial && (
              <Chip
                label={`Mis propuestas: ${filtered?.length || 0}`}
                size="small"
                color="primary"
                sx={{
                  height: 44,
                  borderRadius: "12px",
                  fontWeight: 600,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                }}
              />
            )}
            <Chip
              label={`Total: ${filtered?.length || 0}`}
              sx={{
                height: 44,
                borderRadius: "12px",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                background: theme.palette.background.paper,
                border: "1px solid",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              }}
            />
            <Tooltip title="Refrescar">
              <IconButton
                onClick={() => {
                  if (proposals) setLocalProposals(proposals);
                }}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(0, 175, 163, 0.1)"
                        : "rgba(0, 95, 101, 0.05)",
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {Object.values(grouped).every((arr) => arr.length === 0) ? (
        <EmptyState
          title={
            search 
              ? "Sin resultados" 
              : isCommercialRole 
                ? "No tienes propuestas asignadas" 
                : "Sin propuestas"
          }
          description={
            search 
              ? "Prueba con otros términos" 
              : isCommercialRole 
                ? "Tus propuestas aparecerán aquí cuando te sean asignadas" 
                : "Añade tu primera propuesta"
          }
          actionLabel={!isCommercialRole ? "Nueva Propuesta" : undefined}
          onAction={!isCommercialRole ? () => onEdit(null) : undefined}
        />
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
              },
              gap: { xs: 1.5, sm: 2, md: 2.5 },
              width: "100%",
            }}
          >
            {Object.entries(grouped).map(([status, items]) => {
              const visibleItems = getVisibleItems(status, items);
              const showMoreButton = hasMore(status, items);
              const showLessButton = canShowLess(status);
              const remaining = items.length - visibleItems.length;
              const showing = visibleItems.length;
              const total = items.length;

              return (
                <Box key={status} sx={{ minWidth: 0 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: "16px",
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.1)"
                          : "rgba(0, 175, 163, 0.2)",
                      background:
                        theme.palette.mode === "dark"
                          ? "rgba(0, 95, 101, 0.08)"
                          : "rgba(0, 250, 210, 0.06)",
                      minHeight: 200,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        color="text.secondary"
                        noWrap
                        sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                      >
                        {PROPOSAL_STATUS_LABELS[status]}
                      </Typography>
                      <Chip
                        label={items.length}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: { xs: "0.65rem", sm: "0.75rem" } }}
                      />
                    </Box>

                    <Droppable droppableId={status}>
                      {(provided, snapshot) => (
                        <Stack
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          spacing={1.5}
                          sx={{
                            flex: 1,
                            minHeight: 80,
                            background: snapshot.isDraggingOver
                              ? theme.palette.mode === "dark"
                                ? "rgba(0, 225, 210, 0.15)"
                                : "rgba(0, 225, 210, 0.12)"
                              : "transparent",
                            borderRadius: "12px",
                            border: snapshot.isDraggingOver
                              ? `2px dashed ${theme.palette.primary.light || "#00e1d2"}`
                              : "2px dashed transparent",
                            transition: "all 0.2s",
                            p: 1,
                          }}
                        >
                          {visibleItems.map((proposal, index) => (
                            <Draggable
                              key={proposal.id}
                              draggableId={proposal.id}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <Card
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  elevation={0}
                                  sx={{
                                    borderRadius: "12px",
                                    border: "1px solid",
                                    borderColor: snapshot.isDragging
                                      ? theme.palette.primary.main
                                      : theme.palette.mode === "dark"
                                        ? "rgba(0, 225, 210, 0.25)"
                                        : "rgba(0, 175, 163, 0.3)",
                                    background: snapshot.isDragging
                                      ? theme.palette.mode === "dark"
                                        ? "rgba(0, 225, 210, 0.25)"
                                        : "rgba(0, 250, 210, 0.2)"
                                      : theme.palette.mode === "dark"
                                        ? "rgba(0, 175, 163, 0.2)"
                                        : "rgba(0, 250, 210, 0.15)",
                                    cursor: snapshot.isDragging
                                      ? "grabbing"
                                      : "default",
                                    transition:
                                      "box-shadow 0.2s, transform 0.2s, background 0.2s",
                                    boxShadow: snapshot.isDragging
                                      ? `0 8px 24px rgba(0, 175, 163, 0.3)`
                                      : "none",
                                    "&:hover": {
                                      boxShadow: snapshot.isDragging
                                        ? `0 8px 24px rgba(0, 175, 163, 0.3)`
                                        : `0 4px 16px rgba(0, 175, 163, 0.15)`,
                                      transform: snapshot.isDragging
                                        ? "none"
                                        : "translateY(-2px)",
                                    },
                                  }}
                                >
                                  <CardContent
                                    sx={{ p: { xs: 1.5, sm: 2 }, "&:last-child": { pb: { xs: 1.5, sm: 2 } } }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "flex-start",
                                        mb: 1,
                                      }}
                                    >
                                      <Box
                                        {...provided.dragHandleProps}
                                        sx={{
                                          mr: 1,
                                          mt: 0.5,
                                          color: "text.disabled",
                                          cursor: "grab",
                                          display: "flex",
                                          alignItems: "center",
                                          "&:hover": {
                                            color: theme.palette.primary.main,
                                          },
                                          "&:active": { cursor: "grabbing" },
                                        }}
                                      >
                                        <DragIndicator sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                      </Box>

                                      <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                          variant="body2"
                                          fontWeight={600}
                                          sx={{
                                            cursor: "pointer",
                                            wordBreak: "break-word",
                                            fontSize: { xs: "0.75rem", sm: "0.85rem" },
                                          }}
                                          onClick={() => onView(proposal)}
                                        >
                                          {proposal.title || "Sin título"}
                                        </Typography>

                                        {proposal.version && (
                                          <Typography
                                            variant="caption"
                                            color="primary"
                                            sx={{
                                              display: "block",
                                              fontWeight: 600,
                                              mb: 0.5,
                                              fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                            }}
                                          >
                                            v{proposal.version}
                                          </Typography>
                                        )}

                                        {/* Mostrar comercial asignado */}
                                        {proposal.commercialName && (
                                          <Chip
                                            label={`🏷️ ${proposal.commercialName}`}
                                            size="small"
                                            variant="outlined"
                                            sx={{ 
                                              mb: 0.5, 
                                              fontSize: { xs: 9, sm: 10 },
                                              borderColor: theme.palette.info.main,
                                              color: theme.palette.info.main,
                                            }}
                                          />
                                        )}

                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          display="block"
                                          sx={{
                                            mb: 0.5,
                                            wordBreak: "break-word",
                                            fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                          }}
                                        >
                                          {proposal.address?.street
                                            ? `${proposal.address.street}, ${proposal.address.city || ""} ${proposal.address.province || ""}`
                                            : "Sin dirección"}
                                        </Typography>

                                        <Box sx={{ mt: 0.5 }}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                              display: "block",
                                              wordBreak: "break-word",
                                              fontSize: { xs: "0.6rem", sm: "0.7rem" },
                                            }}
                                          >
                                            {proposal.clientName ||
                                              "Sin cliente"}
                                          </Typography>
                                        </Box>

                                        {proposal.hasInvoice && (
                                          <Chip
                                            label={
                                              proposal.invoiceLink
                                                ? "⚡ Factura"
                                                : "⚡ Pendiente factura"
                                            }
                                            size="small"
                                            color={
                                              proposal.invoiceLink
                                                ? "warning"
                                                : "default"
                                            }
                                            variant="outlined"
                                            sx={{ mt: 0.5, fontSize: { xs: 9, sm: 10 } }}
                                          />
                                        )}
                                      </Box>
                                    </Box>

                                    <Box
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        ml: 4,
                                        gap: 1,
                                      }}
                                    >
                                      <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Ver detalle">
                                          <IconButton
                                            size="small"
                                            onClick={() => onView(proposal)}
                                            sx={{
                                              color: theme.palette.info.main,
                                            }}
                                          >
                                            <Visibility sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                          <IconButton
                                            size="small"
                                            onClick={() => onEdit(proposal)}
                                            sx={{
                                              color: theme.palette.primary.main,
                                            }}
                                          >
                                            <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                          <IconButton
                                            size="small"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setDeleteTarget(proposal);
                                            }}
                                            sx={{
                                              color: theme.palette.error.main,
                                            }}
                                          >
                                            <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    </Box>
                                  </CardContent>
                                </Card>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </Stack>
                      )}
                    </Droppable>

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                      }}
                    >
                      {showMoreButton && (
                        <Button
                          fullWidth
                          size="small"
                          onClick={() => showMore(status)}
                          endIcon={<ExpandMore />}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: theme.palette.primary.main,
                            borderColor: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(0, 175, 163, 0.1)"
                                  : "rgba(0, 95, 101, 0.05)",
                            },
                          }}
                          variant="outlined"
                        >
                          Ver {Math.min(remaining, ITEMS_PER_PAGE)} más (
                          {showing}/{total})
                        </Button>
                      )}
                      {showLessButton && (
                        <Button
                          fullWidth
                          size="small"
                          onClick={() => showLess(status)}
                          endIcon={<ExpandLess />}
                          sx={{
                            borderRadius: "12px",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: { xs: "0.65rem", sm: "0.75rem" },
                            color: theme.palette.text.secondary,
                            borderColor: theme.palette.divider,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.05)"
                                  : "rgba(0, 0, 0, 0.03)",
                            },
                          }}
                          variant="outlined"
                        >
                          Ver menos
                        </Button>
                      )}
                    </Box>
                  </Paper>
                </Box>
              );
            })}
          </Box>
        </DragDropContext>
      )}

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Eliminar Propuesta"
        message={`¿Eliminar la propuesta "${deleteTarget?.title}"?`}
        type="delete"
      />
    </Box>
  );
};

export default ProposalList;